'use client'
import { SanityImageAssetDocument } from 'next-sanity'
import { PortableTextObject } from 'sanity'
import { styles, SchemaSectionVariants } from './SchemaSection.css'
import { SchemaDesktop, SchemaMobile } from './Schema/Schema'
import { useEffect, useRef, useState } from 'react'
import { AppList } from '@otomoro/components/shared/AppList'
import { Step } from './Step/Step'
import { StepDesktop } from './Step/StepDesktop'
import { SectionHeader } from '@otomoro/components/shared/SectionHeader'
import { AnimatePresence, motion } from 'framer-motion'

interface SchemaSectionProps extends SchemaSectionVariants {
	tag: string | undefined
	headline: PortableTextObject | undefined
	steps?: {
		title: string
		description: PortableTextObject
		thumbnail: SanityImageAssetDocument
	}[]
}

export const SchemaSection: React.FC<SchemaSectionProps> = ({ tag, headline, steps }) => {
	const [openIdx, setOpenIdx] = useState(0)
	const stepsList = useRef<HTMLUListElement>(null)
	const [maxHeight, setMaxHeight] = useState(0)

	const toggleDetails = (
		e: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<SVGElement, MouseEvent>,
		idx: number
	) => {
		e.preventDefault()
		if (openIdx === idx) {
			setOpenIdx(0)
		} else {
			setOpenIdx(idx)
		}
	}

	function handleMaxHieght(): void {
		const list = stepsList.current
		const childrenList = Array.from(list?.children || []) as HTMLLIElement[]

		childrenList?.forEach((child: Element) => {
			const htmlChild = child as HTMLElement
			if (htmlChild.offsetHeight > maxHeight) {
				setMaxHeight(htmlChild.offsetHeight)
			}
		})

		if (list && maxHeight > 0) {
			list.style.height = `${maxHeight}px`
		}
	}

	useEffect(() => {
		if (typeof window === 'undefined') return

		handleMaxHieght()
	})

	const { base, inner, header, heading, stepsContainer, stepsDesktop } = styles()

	return (
		<section className={base()}>
			<div className={inner()}>
				<aside className={header()}>
					<SectionHeader tag={tag} title={headline as PortableTextObject} theme="dark" noPx className={heading()} tagClass='max-md:mx-auto' />
					<AppList noWrapper className={stepsDesktop()} ref={stepsList}>
						{steps &&
							steps.length > 0 &&
							steps.map((step, index) => {
								if (openIdx !== index) return null
								return (
									<StepDesktop
										key={index}
										title={step.title}
										description={step.description}
										thumbnail={step.thumbnail}
										index={index}
										openIdx={openIdx}
									/>
								)
							})
						}
					</AppList>
				</aside>
				<SchemaDesktop activeIdx={openIdx} toggleDetails={toggleDetails} steps={steps as any} />
				<SchemaMobile activeIdx={openIdx} toggleDetails={toggleDetails} steps={steps as any} />
				<AppList className={stepsContainer()}>
					{steps &&
						steps.length > 0 &&
						steps.map((step, index) => (
							<Step
								key={index}
								title={step.title}
								description={step.description}
								thumbnail={step.thumbnail}
								index={index}
								openIdx={openIdx}
								toggleDetails={toggleDetails}
							/>
						))}
				</AppList>
			</div>
		</section>
	)
}
